import { useState, useCallback, useRef } from 'react';
// eslint-disable-next-line no-unused-vars
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import {
  Upload,
  FileUp,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Loader2,
  FileText,
  Trash2,
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

// ---------------------------------------------------------------------------
// CSV parsing
// ---------------------------------------------------------------------------
function parseCSV(text) {
  const lines = text.split('\n').filter((line) => line.trim());
  if (lines.length < 2) return { headers: [], rows: [] };

  const headers = parseCSVLine(lines[0]);
  const rows = lines.slice(1).map((line) => {
    const values = parseCSVLine(line);
    const row = {};
    headers.forEach((h, i) => {
      row[h.trim()] = (values[i] || '').trim();
    });
    return row;
  });

  return { headers: headers.map((h) => h.trim()), rows };
}

function parseCSVLine(line) {
  const values = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    if (char === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === ',' && !inQuotes) {
      values.push(current);
      current = '';
    } else {
      current += char;
    }
  }
  values.push(current);
  return values;
}

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------
// Raw event format (from JSON export or raw CSV)
const RAW_REQUIRED_HEADERS = ['Employee ID', 'Action', 'Timestamp'];
// Paired shift format (from CSV export)
const SHIFT_REQUIRED_HEADERS = ['Employee ID', 'Clock In'];

/**
 * Detect whether CSV rows are in raw event format or paired shift format.
 */
function detectCSVFormat(headers) {
  const hasRaw = RAW_REQUIRED_HEADERS.every((h) => headers.includes(h));
  const hasShift = SHIFT_REQUIRED_HEADERS.every((h) => headers.includes(h));
  if (hasRaw) return 'raw';
  if (hasShift) return 'shift';
  return null;
}

/**
 * Resolve a timestamp from shift CSV columns.
 * Handles two formats:
 *   1. ISO timestamp (new exports): "2026-03-04T14:30:00.000Z" — use directly
 *   2. Locale time (old exports): "14:30" with a separate Date column like "03/04/2026"
 */
function resolveShiftTimestamp(timeValue, dateValue) {
  if (!timeValue) return null;

  // If it already looks like an ISO timestamp (contains 'T' or starts with 4 digits + dash)
  const parsed = new Date(timeValue);
  if (!isNaN(parsed.getTime()) && (timeValue.includes('T') || /^\d{4}-/.test(timeValue))) {
    return timeValue;
  }

  // It's a locale time like "14:30" or "2:30 PM" — combine with date column
  if (dateValue) {
    // Try parsing the date column (handles "03/04/2026", "2026-03-04", etc.)
    const dateParsed = new Date(dateValue);
    if (!isNaN(dateParsed.getTime())) {
      // Extract hours and minutes from the time string
      const timeParts = parseTimeString(timeValue);
      if (timeParts) {
        dateParsed.setHours(timeParts.hours, timeParts.minutes, 0, 0);
        return dateParsed.toISOString();
      }
    }
  }

  // Last resort: try to parse as-is
  return timeValue;
}

/**
 * Parse a time string like "14:30", "2:30 PM", "02:30" into { hours, minutes }.
 */
function parseTimeString(timeStr) {
  if (!timeStr) return null;
  const str = timeStr.trim();

  // 24-hour format: "14:30"
  const match24 = str.match(/^(\d{1,2}):(\d{2})$/);
  if (match24) {
    return { hours: parseInt(match24[1], 10), minutes: parseInt(match24[2], 10) };
  }

  // 12-hour format: "2:30 PM" or "02:30 AM"
  const match12 = str.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (match12) {
    let hours = parseInt(match12[1], 10);
    const minutes = parseInt(match12[2], 10);
    const period = match12[3].toUpperCase();
    if (period === 'PM' && hours !== 12) hours += 12;
    if (period === 'AM' && hours === 12) hours = 0;
    return { hours, minutes };
  }

  return null;
}

/**
 * Convert paired shift CSV rows into raw event rows for uniform processing.
 */
function shiftRowsToEventRows(rows) {
  const events = [];

  for (const row of rows) {
    const userId = row['Employee ID'];
    if (!userId) continue;

    const dateValue = row['Date'] || '';

    // Clock in event
    if (row['Clock In']) {
      const ts = resolveShiftTimestamp(row['Clock In'], dateValue);
      if (ts) {
        events.push({
          'Employee Name': row['Employee Name'] || '',
          'Employee ID': userId,
          'Action': 'clock_in',
          'Timestamp': ts,
          'Method': row['Method'] || '',
          'Sync Status': row['Sync Status'] || 'pending',
          'Break Type ID': '',
          'Has Selfie': 'No',
        });
      }
    }

    // Clock out event
    if (row['Clock Out']) {
      const ts = resolveShiftTimestamp(row['Clock Out'], dateValue);
      if (ts) {
        events.push({
          'Employee Name': row['Employee Name'] || '',
          'Employee ID': userId,
          'Action': 'clock_out',
          'Timestamp': ts,
          'Method': '',
          'Sync Status': row['Sync Status'] || 'pending',
          'Break Type ID': '',
          'Has Selfie': 'No',
        });
      }
    }
  }

  // Sort by timestamp
  events.sort((a, b) => (a['Timestamp'] || '').localeCompare(b['Timestamp'] || ''));
  return events;
}

function validateRow(row, index, existingUserIds) {
  const errors = [];

  if (!row['Employee ID']) {
    errors.push(`Row ${index + 1}: Missing Employee ID`);
  } else if (!existingUserIds.has(row['Employee ID'])) {
    errors.push(`Row ${index + 1}: Unknown Employee ID "${row['Employee ID']}"`);
  }

  if (!row['Action']) {
    errors.push(`Row ${index + 1}: Missing Action`);
  } else if (!['clock_in', 'clock_out', 'break_start', 'break_end'].includes(row['Action'])) {
    errors.push(`Row ${index + 1}: Invalid Action "${row['Action']}"`);
  }

  if (!row['Timestamp']) {
    errors.push(`Row ${index + 1}: Missing Timestamp`);
  } else {
    const d = new Date(row['Timestamp']);
    if (isNaN(d.getTime())) {
      errors.push(`Row ${index + 1}: Invalid Timestamp "${row['Timestamp']}"`);
    }
  }

  return errors;
}

// ---------------------------------------------------------------------------
// KioskDataImport
// ---------------------------------------------------------------------------
export default function KioskDataImport({ projectId, companyId }) {
  const [file, setFile] = useState(null);
  const [parsedData, setParsedData] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);
  const [validationWarnings, setValidationWarnings] = useState([]);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);

  // -----------------------------------------------------------------------
  // File selection and parsing
  // -----------------------------------------------------------------------
  const handleFileChange = useCallback(async (e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    setFile(selectedFile);
    setImportResult(null);
    setValidationErrors([]);
    setValidationWarnings([]);

    try {
      const text = await selectedFile.text();

      // Support both CSV and JSON
      if (selectedFile.name.endsWith('.json')) {
        const json = JSON.parse(text);
        if (!json.events || !Array.isArray(json.events)) {
          setValidationErrors(['Invalid JSON format: missing "events" array']);
          return;
        }

        // Convert JSON events to CSV-like rows for uniform processing
        const rows = json.events.map((evt) => ({
          'Employee Name': json.employees?.find((e) => e.id === evt.userId)?.displayName || '',
          'Employee ID': evt.userId,
          'Action': evt.action,
          'Timestamp': evt.timestamp,
          'Method': evt.method || '',
          'Sync Status': evt.syncStatus || 'pending',
          'Break Type ID': evt.breakTypeId || '',
          'Has Selfie': evt.hasSelfie ? 'Yes' : 'No',
        }));

        setParsedData({
          headers: Object.keys(rows[0] || {}),
          rows,
          source: 'json',
          deviceInfo: json.device || null,
        });
      } else {
        const { headers, rows } = parseCSV(text);
        const csvFormat = detectCSVFormat(headers);

        if (csvFormat === 'shift') {
          // Convert paired shift CSV to raw event rows
          const eventRows = shiftRowsToEventRows(rows);
          setParsedData({
            headers: Object.keys(eventRows[0] || {}),
            rows: eventRows,
            source: 'csv (shift format)',
          });
        } else if (csvFormat === 'raw') {
          setParsedData({ headers, rows, source: 'csv' });
        } else {
          setValidationErrors([
            'Unrecognised CSV format. Expected either raw event columns (Employee ID, Action, Timestamp) or paired shift columns (Employee ID, Clock In, Clock Out).',
          ]);
          return;
        }
      }
    } catch (err) {
      setValidationErrors([`Failed to parse file: ${err.message}`]);
      setParsedData(null);
    }
  }, []);

  // -----------------------------------------------------------------------
  // Validate parsed data
  // -----------------------------------------------------------------------
  const handleValidate = useCallback(async () => {
    if (!parsedData?.rows?.length) return;

    const errors = [];
    const warnings = [];

    // Check required headers (data is always in raw event format after parsing)
    for (const h of RAW_REQUIRED_HEADERS) {
      if (!parsedData.headers.includes(h)) {
        errors.push(`Missing required column: "${h}"`);
      }
    }

    if (errors.length > 0) {
      setValidationErrors(errors);
      return;
    }

    // Fetch valid employee IDs for this company
    const { data: members } = await supabase
      .from('company_members')
      .select('user_id')
      .eq('company_id', companyId);

    const validUserIds = new Set((members || []).map((m) => m.user_id));

    // Validate each row
    for (let i = 0; i < parsedData.rows.length; i++) {
      const rowErrors = validateRow(parsedData.rows[i], i, validUserIds);
      errors.push(...rowErrors);
    }

    // Check for already-synced rows
    const syncedCount = parsedData.rows.filter(
      (r) => r['Sync Status'] === 'synced'
    ).length;
    if (syncedCount > 0) {
      warnings.push(
        `${syncedCount} row(s) are already marked as "synced" and will be skipped`
      );
    }

    setValidationErrors(errors);
    setValidationWarnings(warnings);

    if (errors.length === 0) {
      toast.success('Validation passed — ready to import');
    } else {
      toast.error(`${errors.length} validation error(s) found`);
    }
  }, [parsedData, companyId]);

  // -----------------------------------------------------------------------
  // Import data
  // -----------------------------------------------------------------------
  const handleImport = useCallback(async () => {
    if (!parsedData?.rows?.length || validationErrors.length > 0) return;

    setImporting(true);
    setImportResult(null);

    const results = { imported: 0, skipped: 0, failed: 0, errors: [] };

    try {
      // Filter out already-synced rows
      const rowsToImport = parsedData.rows.filter(
        (r) => r['Sync Status'] !== 'synced'
      );

      // Group events by user and sort by timestamp
      const byUser = {};
      for (const row of rowsToImport) {
        const uid = row['Employee ID'];
        if (!byUser[uid]) byUser[uid] = [];
        byUser[uid].push(row);
      }

      for (const [userId, userRows] of Object.entries(byUser)) {
        userRows.sort((a, b) => a['Timestamp'].localeCompare(b['Timestamp']));

        for (const row of userRows) {
          try {
            const action = row['Action'];
            const timestamp = new Date(row['Timestamp']).toISOString();

            if (action === 'clock_in') {
              // Dedup: check if a check-in already exists at this exact time
              const { data: duplicate } = await supabase
                .from('site_checkins')
                .select('id')
                .eq('user_id', userId)
                .eq('project_id', projectId)
                .eq('check_in_time', timestamp)
                .maybeSingle();

              if (duplicate) {
                results.skipped++;
                continue;
              }

              const { error: insertErr } = await supabase
                .from('site_checkins')
                .insert({
                  user_id: userId,
                  project_id: projectId,
                  check_in_time: timestamp,
                  status: 'checked_in',
                  check_in_method: 'kiosk_import',
                });

              if (insertErr) {
                results.failed++;
                results.errors.push(`Clock-in for ${userId}: ${insertErr.message}`);
              } else {
                results.imported++;
              }
            } else if (action === 'clock_out') {
              // Find the most recent check-in without a clock-out for this user
              const { data: activeCheckin } = await supabase
                .from('site_checkins')
                .select('id, check_out_time')
                .eq('user_id', userId)
                .eq('project_id', projectId)
                .is('check_out_time', null)
                .eq('status', 'checked_in')
                .order('check_in_time', { ascending: false })
                .limit(1)
                .maybeSingle();

              if (!activeCheckin) {
                // Maybe already closed — check if a check-in exists with this exact clock-out time
                const { data: alreadyClosed } = await supabase
                  .from('site_checkins')
                  .select('id')
                  .eq('user_id', userId)
                  .eq('project_id', projectId)
                  .eq('check_out_time', timestamp)
                  .maybeSingle();

                if (alreadyClosed) {
                  results.skipped++;
                } else {
                  results.skipped++;
                }
                continue;
              }

              const { error: updateErr } = await supabase
                .from('site_checkins')
                .update({ check_out_time: timestamp })
                .eq('id', activeCheckin.id);

              if (updateErr) {
                results.failed++;
                results.errors.push(`Clock-out for ${userId}: ${updateErr.message}`);
              } else {
                results.imported++;
              }
            } else if (action === 'break_start') {
              // Find active check-in
              const { data: checkin } = await supabase
                .from('site_checkins')
                .select('id')
                .eq('user_id', userId)
                .eq('project_id', projectId)
                .is('check_out_time', null)
                .eq('status', 'checked_in')
                .maybeSingle();

              if (!checkin) {
                results.skipped++;
                continue;
              }

              // Dedup: check for existing break at this exact start time
              const { data: dupBreak } = await supabase
                .from('shift_breaks')
                .select('id')
                .eq('user_id', userId)
                .eq('start_time', timestamp)
                .maybeSingle();

              if (dupBreak) {
                results.skipped++;
                continue;
              }

              const { error: bErr } = await supabase
                .from('shift_breaks')
                .insert({
                  checkin_id: checkin.id,
                  user_id: userId,
                  break_type_id: row['Break Type ID'] || null,
                  start_time: timestamp,
                });

              if (bErr) {
                results.failed++;
                results.errors.push(`Break start for ${userId}: ${bErr.message}`);
              } else {
                results.imported++;
              }
            } else if (action === 'break_end') {
              const { data: activeBreak } = await supabase
                .from('shift_breaks')
                .select('id, start_time')
                .eq('user_id', userId)
                .is('end_time', null)
                .maybeSingle();

              if (!activeBreak) {
                results.skipped++;
                continue;
              }

              const durationMin = Math.round(
                (new Date(timestamp).getTime() - new Date(activeBreak.start_time).getTime()) / 60000
              );

              const { error: bUpErr } = await supabase
                .from('shift_breaks')
                .update({
                  end_time: timestamp,
                  duration_minutes: durationMin,
                })
                .eq('id', activeBreak.id);

              if (bUpErr) {
                results.failed++;
                results.errors.push(`Break end for ${userId}: ${bUpErr.message}`);
              } else {
                results.imported++;
              }
            }
          } catch (err) {
            results.failed++;
            results.errors.push(`${row['Action']} for ${userId}: ${err.message}`);
          }
        }
      }

      setImportResult(results);

      if (results.failed === 0) {
        toast.success(`Import complete: ${results.imported} records imported`);
      } else {
        toast.warning(
          `Import done with issues: ${results.imported} imported, ${results.failed} failed`
        );
      }
    } catch (err) {
      toast.error(`Import failed: ${err.message}`);
      setImportResult({ ...results, errors: [err.message] });
    } finally {
      setImporting(false);
    }
  }, [parsedData, validationErrors, projectId]);

  // -----------------------------------------------------------------------
  // Clear
  // -----------------------------------------------------------------------
  const handleClear = useCallback(() => {
    setFile(null);
    setParsedData(null);
    setValidationErrors([]);
    setValidationWarnings([]);
    setImportResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const importableCount = parsedData
    ? parsedData.rows.filter((r) => r['Sync Status'] !== 'synced').length
    : 0;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Upload className="h-5 w-5 text-slate-600" />
            <CardTitle className="text-base">Import Kiosk Data</CardTitle>
          </div>
          {file && (
            <Button variant="ghost" size="sm" onClick={handleClear} className="gap-1.5">
              <Trash2 className="w-3.5 h-3.5" />
              Clear
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-slate-500">
          Import clock events exported from an offline kiosk device. Supports CSV and JSON formats.
        </p>

        {/* File upload */}
        {!file && (
          <label className="flex flex-col items-center justify-center gap-3 py-8 px-4 border-2 border-dashed border-slate-200 rounded-xl bg-slate-50 hover:bg-slate-100 cursor-pointer transition-colors">
            <FileUp className="w-8 h-8 text-slate-400" />
            <div className="text-center">
              <p className="text-sm font-medium text-slate-600">
                Drop a CSV or JSON file here, or click to browse
              </p>
              <p className="text-xs text-slate-400 mt-1">
                Use files exported from the kiosk admin panel
              </p>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.json"
              onChange={handleFileChange}
              className="hidden"
            />
          </label>
        )}

        {/* File info */}
        {file && parsedData && (
          <motion.div
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-3"
          >
            <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border border-slate-100">
              <FileText className="w-5 h-5 text-slate-500 flex-none" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-700 truncate">{file.name}</p>
                <p className="text-xs text-slate-400">
                  {parsedData.rows.length} total rows
                  {parsedData.source === 'json' && parsedData.deviceInfo && (
                    <span> from device: {parsedData.deviceInfo.name || parsedData.deviceInfo.id}</span>
                  )}
                </p>
              </div>
              <Badge variant="outline" className="flex-none">
                {parsedData.source.toUpperCase()}
              </Badge>
            </div>

            {/* Preview summary */}
            <div className="grid grid-cols-3 gap-3">
              <div className="text-center p-2 rounded-lg bg-slate-50">
                <p className="text-lg font-bold text-slate-800 tabular-nums">{parsedData.rows.length}</p>
                <p className="text-xs text-slate-400">Total Events</p>
              </div>
              <div className="text-center p-2 rounded-lg bg-emerald-50">
                <p className="text-lg font-bold text-emerald-700 tabular-nums">{importableCount}</p>
                <p className="text-xs text-emerald-600">To Import</p>
              </div>
              <div className="text-center p-2 rounded-lg bg-slate-50">
                <p className="text-lg font-bold text-slate-500 tabular-nums">
                  {parsedData.rows.length - importableCount}
                </p>
                <p className="text-xs text-slate-400">Already Synced</p>
              </div>
            </div>

            {/* Validation errors */}
            <AnimatePresence>
              {validationErrors.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="rounded-lg border border-red-200 bg-red-50 p-3 space-y-1"
                >
                  <p className="text-sm font-medium text-red-700 flex items-center gap-1.5">
                    <XCircle className="w-4 h-4" />
                    {validationErrors.length} validation error{validationErrors.length !== 1 ? 's' : ''}
                  </p>
                  <ul className="text-xs text-red-600 space-y-0.5 ml-5 max-h-32 overflow-y-auto">
                    {validationErrors.slice(0, 10).map((err, i) => (
                      <li key={i}>{err}</li>
                    ))}
                    {validationErrors.length > 10 && (
                      <li className="font-medium">...and {validationErrors.length - 10} more</li>
                    )}
                  </ul>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Validation warnings */}
            <AnimatePresence>
              {validationWarnings.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="rounded-lg border border-amber-200 bg-amber-50 p-3"
                >
                  {validationWarnings.map((w, i) => (
                    <p key={i} className="text-xs text-amber-700 flex items-center gap-1.5">
                      <AlertTriangle className="w-3.5 h-3.5 flex-none" />
                      {w}
                    </p>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>

            {/* Import result */}
            <AnimatePresence>
              {importResult && (
                <motion.div
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  className={`rounded-lg border p-3 space-y-1.5 ${
                    importResult.failed === 0
                      ? 'border-emerald-200 bg-emerald-50'
                      : 'border-amber-200 bg-amber-50'
                  }`}
                >
                  <p className={`text-sm font-medium flex items-center gap-1.5 ${
                    importResult.failed === 0 ? 'text-emerald-700' : 'text-amber-700'
                  }`}>
                    <CheckCircle2 className="w-4 h-4" />
                    Import complete
                  </p>
                  <div className="flex gap-4 text-xs">
                    <span className="text-emerald-600">{importResult.imported} imported</span>
                    <span className="text-slate-500">{importResult.skipped} skipped</span>
                    {importResult.failed > 0 && (
                      <span className="text-red-500">{importResult.failed} failed</span>
                    )}
                  </div>
                  {importResult.errors.length > 0 && (
                    <ul className="text-xs text-red-600 space-y-0.5 mt-1 max-h-24 overflow-y-auto">
                      {importResult.errors.slice(0, 5).map((err, i) => (
                        <li key={i}>{err}</li>
                      ))}
                    </ul>
                  )}
                </motion.div>
              )}
            </AnimatePresence>

            {/* Action buttons */}
            <div className="flex gap-2">
              <Button
                variant="outline"
                className="gap-1.5"
                onClick={handleValidate}
                disabled={importing}
              >
                <CheckCircle2 className="w-4 h-4" />
                Validate
              </Button>
              <Button
                className="gap-1.5"
                onClick={handleImport}
                disabled={importing || validationErrors.length > 0 || importableCount === 0}
              >
                {importing ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Upload className="w-4 h-4" />
                )}
                {importing ? 'Importing...' : `Import ${importableCount} Events`}
              </Button>
            </div>
          </motion.div>
        )}
      </CardContent>
    </Card>
  );
}
